import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import MainLayout from '../components/layout/MainLayout';
import {
  Modal,
  Card,
  Typography,
  TextField,
  Button,
  Alert,
  IconButton,
  Fade,
  Paper,
  Chip,
  Stack,
  Container,
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// APIs
import { 
  fetchSchoolsAsync, 
  deleteSchoolAsync, 
  createSchoolAsync, 
  updateSchoolAsync,
  setError 
} from '../redux/reducer/schools/schoolSlice';

const Schools = () => {
  const dispatch = useDispatch();

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const schools = useSelector((state) => state.school.list);
  const loading = useSelector((state) => state.school.loading);
  const ErrorState = useSelector((state) => state.school.error);

  // New state variable for success alert
  const [successAlert, setSuccessAlert] = useState(false);

  useEffect(() => {
    dispatch(fetchSchoolsAsync());
  }, [dispatch]);

  const handleOpen = () => {
    setOpen(true);
    setSaved(false);
  };

  const handleClose = () => {
    setOpen(false);
    setSaved(false);
    setSaving(false);
    setName('');
    setCode('');
    setDescription('');
  };

  const openEditModal = (school) => {
    setIsEditModalOpen(true);
    setSelectedSchool(school);
  };

  const closeEditModal = () => {
    setSelectedSchool(null);
    setIsEditModalOpen(false);
  };

  const handleOpenDeleteModal = (school) => {
    setSelectedSchool(school);
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
  };

  const handleAdd = () => {
    setSaving(true);
    if (!name || !code) {
      dispatch(setError('Name and code are required'));
      setSaving(false);
      return;
    }
    try {
      const schoolData = {
        name,
        code,
        description
      };
      dispatch(createSchoolAsync(schoolData))
        .then(() => {
          setSaving(false);
          dispatch(fetchSchoolsAsync());
          setSuccessAlert(true); // Show success alert
          setName('');
          setCode('');
          setDescription('');
          handleClose();
        })
        .catch((error) => {
          setSaving(false);
          setSaved(false);
          dispatch(setError(error.message));
          console.error(error);
        });
    } catch (error) {
      setSaving(false);
      setSaved(false);
      dispatch(setError(error.message));
      console.log(error);
    }
  };

  const handleDeleteSchool = () => {
    if (selectedSchool) {
      dispatch(deleteSchoolAsync(selectedSchool._id))
        .then(() => {
          console.log(`deleted ${selectedSchool._id}`);
          dispatch(fetchSchoolsAsync());
          handleCloseDeleteModal();
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  const handleUpdateSchool = async (event) => {
    event.preventDefault();

    if (selectedSchool) {
      if (!selectedSchool.name || !selectedSchool.code) {
        dispatch(setError('Name and code are required'));
        return;
      }

      const schoolData = {
        name: selectedSchool.name,
        code: selectedSchool.code,
        description: selectedSchool.description
      };

      dispatch(updateSchoolAsync(selectedSchool._id, schoolData))
        .then((response) => {
          if (response) {
            setSaved(true);
            dispatch(fetchSchoolsAsync());
            setTimeout(() => {
              closeEditModal();
              setSaved(false);
            }, 1000);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  if (loading && !schools.length) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress /> <Typography ml={2}>Loading...</Typography>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Box>
              <Typography
                variant="h4"
                fontWeight="700"
                gutterBottom
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Schools Management
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage educational institutions and their details
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpen}
              sx={{
                borderRadius: 2,
                px: 3,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                },
              }}
            >
              Add School
            </Button>
          </Stack>
        </Box>
      
        {/* Success Alert */}
        {successAlert && (
          <Fade in={true}>
            <Alert 
              severity="success" 
              onClose={() => setSuccessAlert(false)}
              sx={{ mb: 3, borderRadius: 2 }}
            >
              Operation completed successfully!
            </Alert>
          </Fade>
        )}

        {/* Error Alert */}
        {ErrorState && (
          <Fade in={true}>
            <Alert 
              severity="error" 
              onClose={() => dispatch(setError(null))}
              sx={{ mb: 3, borderRadius: 2 }}
            >
              {ErrorState}
            </Alert>
          </Fade>
        )}

        {/* Add School Modal */}
        <Modal
          open={open}
          onClose={handleClose}
          sx={{
            backdropFilter: 'blur(4px)',
          }}
        >
          <Fade in={open}>
            <Paper
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '90%', sm: 500 },
                borderRadius: 3,
                boxShadow: 24,
                maxHeight: '90vh',
                overflow: 'auto',
              }}
            >
              {/* Modal Header */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  p: 3,
                  color: 'white',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h6" fontWeight="600">
                  Create New School
                </Typography>
                <IconButton onClick={handleClose} sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Modal Body */}
              <Box sx={{ p: 3 }}>
                {ErrorState && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    {ErrorState}
                  </Alert>
                )}
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label="School Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                  <TextField
                    fullWidth
                    label="School Code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    required
                    helperText="Enter a unique code for the school (e.g., SCH001)"
                  />
                  <TextField
                    fullWidth
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    multiline
                    rows={3}
                    placeholder="Optional description..."
                  />
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={handleClose}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleAdd}
                      disabled={saving}
                      sx={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                        },
                      }}
                    >
                      {saving ? (
                        <>
                          <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                          Saving...
                        </>
                      ) : saved ? (
                        'Saved!'
                      ) : (
                        'Create School'
                      )}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Paper>
          </Fade>
        </Modal>

        {/* Schools Table */}
        <Card sx={{ borderRadius: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>S/N</TableCell>
                  <TableCell>School Name</TableCell>
                  <TableCell>School Code</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : schools && schools.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Typography variant="subtitle1" color="text.secondary">
                        No schools available
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Click "Add School" to create your first school
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  schools.map((school, index) => (
                    <TableRow
                      key={school._id}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(99, 102, 241, 0.04)',
                        },
                      }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">
                          {school.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={school.code}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(99, 102, 241, 0.1)',
                            color: 'primary.main',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>{school.description || '-'}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <IconButton
                            size="small"
                            onClick={() => openEditModal(school)}
                            sx={{
                              color: 'primary.main',
                              '&:hover': {
                                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                              },
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDeleteModal(school)}
                            sx={{
                              color: 'error.main',
                              '&:hover': {
                                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                              },
                            }}
                          >
                            <DeleteForeverIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Edit School Modal */}
        {selectedSchool && (
          <Modal
            open={isEditModalOpen}
            onClose={closeEditModal}
            sx={{ backdropFilter: 'blur(4px)' }}
          >
            <Fade in={isEditModalOpen}>
              <Paper
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: { xs: '90%', sm: 500 },
                  borderRadius: 3,
                  boxShadow: 24,
                }}
              >
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    p: 3,
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="h6" fontWeight="600">
                    Edit School
                  </Typography>
                  <IconButton onClick={closeEditModal} sx={{ color: 'white' }}>
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Box sx={{ p: 3 }}>
                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      label="School Name"
                      value={selectedSchool.name}
                      onChange={(e) => setSelectedSchool({ ...selectedSchool, name: e.target.value })}
                      required
                    />
                    <TextField
                      fullWidth
                      label="School Code"
                      value={selectedSchool.code}
                      onChange={(e) => setSelectedSchool({ ...selectedSchool, code: e.target.value.toUpperCase() })}
                      required
                      helperText="Enter a unique code for the school"
                    />
                    <TextField
                      fullWidth
                      label="Description"
                      value={selectedSchool.description || ''}
                      onChange={(e) => setSelectedSchool({ ...selectedSchool, description: e.target.value })}
                      multiline
                      rows={3}
                    />
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <Button variant="outlined" onClick={closeEditModal}>
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleUpdateSchool}
                        sx={{
                          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                          },
                        }}
                      >
                        {loading ? (
                          <>
                            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                            Saving...
                          </>
                        ) : saved ? (
                          'Saved!'
                        ) : (
                          'Update School'
                        )}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Fade>
          </Modal>
        )}

        {/* Delete Confirmation Modal */}
        <Modal
          open={openDeleteModal}
          onClose={handleCloseDeleteModal}
          sx={{ backdropFilter: 'blur(4px)' }}
        >
          <Fade in={openDeleteModal}>
            <Paper
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '90%', sm: 400 },
                borderRadius: 3,
                boxShadow: 24,
                textAlign: 'center',
                p: 4,
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}
              >
                <WarningAmberIcon sx={{ fontSize: 32, color: 'error.main' }} />
              </Box>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Delete School?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Are you sure you want to delete this school? This action cannot be undone.
              </Typography>
              {selectedSchool && (
                <Box
                  sx={{
                    backgroundColor: 'background.default',
                    p: 2,
                    borderRadius: 2,
                    mb: 3,
                  }}
                >
                  <Typography variant="body2" fontWeight="600">
                    {selectedSchool.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedSchool.code}
                  </Typography>
                </Box>
              )}
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button variant="outlined" onClick={handleCloseDeleteModal}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeleteSchool}
                  startIcon={<DeleteForeverIcon />}
                >
                  Delete School
                </Button>
              </Stack>
            </Paper>
          </Fade>
        </Modal>
      </Container>
    </MainLayout>
  );
};

export default Schools;
